import type { MonitoringUnitsTrend } from "../../../shared/monitoringWorkflowsReport";
import { startOfMonthUtcMonthsAgo } from "../../../shared/tinesUnitsTrend";

type TinesUnitsTrendColumn = {
  periodStart: number;
  total: number;
  isCurrent: boolean;
  // Only the current month carries a projection; null when it's too early in
  // the month to project.
  projectedTotal: number | null;
};

export type TinesUnitsTrendChart = {
  columns: TinesUnitsTrendColumn[];
  currentTotal: number;
  lastMonthTotal: number | null;
  projectedTotal: number | null;
};

// Turns the sparse monthly ledger into a dense run of columns from the earliest
// month with usage through the open month, so idle months in between read as
// gaps at zero rather than vanishing and closing up the axis. Months before the
// earliest row are left out: the ledger simply has no history there.
export function buildTinesUnitsTrendChart(
  trend: MonitoringUnitsTrend
): TinesUnitsTrendChart {
  const totals = new Map<number, number>();
  for (const point of trend.months) totals.set(point.periodStart, point.total);

  const current = trend.currentPeriodStart;
  const earliest =
    trend.months.length > 0 ? trend.months[0]!.periodStart : current;
  const start = Math.min(earliest, current);

  const columns: TinesUnitsTrendColumn[] = [];
  for (
    let period = start;
    period <= current;
    period = startOfMonthUtcMonthsAgo(-1, period)
  ) {
    const isCurrent = period === current;
    columns.push({
      periodStart: period,
      total: totals.get(period) ?? 0,
      isCurrent,
      projectedTotal: isCurrent ? trend.projectedTotal : null,
    });
  }

  const previous = startOfMonthUtcMonthsAgo(1, current);
  const lastMonthTotal = previous >= start ? (totals.get(previous) ?? 0) : null;

  return {
    columns,
    currentTotal: totals.get(current) ?? 0,
    lastMonthTotal,
    projectedTotal: trend.projectedTotal,
  };
}
